/**
 * V17.325 — the permission list that opens `/customer-accounts`.
 *
 * This exists because the list lived in THREE places that had to agree —
 * the route gate in `App.tsx`, the nav entry in the main sidebar, and the AR
 * workspace entry in the AR sidebar — and they drifted every time. (V17.523: those sidebars
 * are gone; the one menu registry `src/app/navigation.ts` imports this list.)
 * V17.321 shipped in two halves precisely because a change to one was not a
 * change to the others. Import this instead of retyping the array.
 *
 * Its backend counterpart is `ACCOUNT_READ_PERMISSIONS` in
 * `backend/app/api/v1/endpoints/customer_accounts.py`, which guards the five
 * GET endpoints. The two are deliberately NOT identical: the backend list also
 * carries the three `logistics.*` codes, because the TSO Dashboard and the
 * site-check panel read accounts through the API. Those seats have no reason
 * to see this page in their nav, so they are omitted here — adding them would
 * widen the UI, not fix an enforcement gap. Backend enforces; this list is the
 * nav/route affordance, and it must stay a SUBSET of the backend list.
 *
 * - `customer_accounts.view` — seeded by V17.324 and granted to the 19 roles
 *   that can reach the page (prod read-back 2026-08-14: 19/19, 0 missing).
 *   It replaces `ar.dashboard.{officer,leader}.view`, the DASHBOARD permission
 *   V17.321 had to use as a stand-in for account-book access.
 * - The four write codes stay because anyone who may modify an account may
 *   read it, and because that membership comes from live permissions rather
 *   than the V17.324 seed list — so a writer role that missed the seed keeps
 *   working instead of 403ing.
 *
 * Narrowing this to `customer_accounts.view` alone is a follow-up, and only
 * after a prod read-back proves no role holds a write code without it.
 */
export const ACCOUNT_READ_PERMISSIONS = [
  'customer_accounts.view',
  'commercial.pipeline.view',
  'commercial.onboarding.view',
  'customers.sync',
  'users.edit',
] as const;
